import HomeSectionsEditor from '../../components/HomeSectionsEditor';
import SkincareBanner, { SKINCARE_DEFAULTS, skincareLink } from '../../components/SkincareBanner';
import HomeBookCard, { HOME_BOOK_DEFAULTS } from '../../components/HomeBookCard';
import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Save, Plus, Trash2, Eye, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import ImagePositionEditor from '../../components/ImagePositionEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [homeHero, setHomeHero] = useState({ badge: '', title: '', subtitle: '', description: '', heroImage: '' });
  const [sections, setSections] = useState(null);
  const [bookInfo, setBookInfo] = useState({ title: '', author: '', price: 0, currency: 'USD', priceDisplay: '', description: '', coverImage: '', purchase_link: '', features: [] });
  const [aboutContent, setAboutContent] = useState({ bio: '', image: '', certifications: [], mission: '' });
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  const [pageContents, setPageContents] = useState({});

  const pageKeys = ['booking', 'contact', 'qa', 'downloads', 'blog', 'book'];

  useEffect(() => { fetchAllContent(); }, []);

  const fetchAllContent = async () => {
    try {
      const [homeHeroRes, settingsRes, faqsRes, sectionsRes, bookRes, ...pageRes] = await Promise.all([
        axios.get(`${API_URL}/home-hero`),
        axios.get(`${API_URL}/admin/settings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/faqs`),
        axios.get(`${API_URL}/site-sections`),
        axios.get(`${API_URL}/book/info`),
        ...pageKeys.map(key => axios.get(`${API_URL}/page-content/${key}`))
      ]);

      setHomeHero(homeHeroRes.data);
      setFaqs(faqsRes.data);
      setSections(sectionsRes.data);
      setBookInfo(bookRes.data);

      const settings = settingsRes.data;
      const aboutSetting = settings.find(s => s.key === 'about_lindsey');
      if (aboutSetting) setAboutContent(aboutSetting.value);
      const testimonialsSetting = settings.find(s => s.key === 'testimonials');
      if (testimonialsSetting) setTestimonials(testimonialsSetting.value);

      const contents = {};
      pageKeys.forEach((key, i) => { contents[key] = pageRes[i].data; });
      setPageContents(contents);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, targetField) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/admin/upload-image`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      const imageUrl = response.data.url;
      if (targetField === 'homeHero') {
        const updated = { ...homeHero, heroImage: imageUrl };
        setHomeHero(updated);
        await axios.put(`${API_URL}/admin/settings/home_hero`, { value: updated }, { headers: getAuthHeader() });
      } else if (targetField === 'homeBookCover' || targetField === 'homeBookAuthor') {
        const field = targetField === 'homeBookCover' ? 'coverImage' : 'authorImage';
        setHomeHero(prev => ({ ...prev, bookCard: { ...prev.bookCard, [field]: imageUrl } }));
      } else if (targetField === 'skincareBanner') {
        setSections(prev => ({ ...prev, skincare: { ...prev?.skincare, imageUrl } }));
      } else if (targetField === 'bookCover') {
        const updated = { ...bookInfo, coverImage: imageUrl };
        setBookInfo(updated);
        await axios.put(`${API_URL}/admin/settings/book_info`, { value: updated }, { headers: getAuthHeader() });
      } else if (targetField === 'aboutImage') {
        const updated = { ...aboutContent, image: imageUrl };
        setAboutContent(updated);
        await axios.put(`${API_URL}/admin/settings/about_lindsey`, { value: updated }, { headers: getAuthHeader() });
      }
      toast.success(targetField === 'skincareBanner' ? 'Image uploaded. Click Save Skincare Banner to publish it.' : targetField === 'homeBookCover' || targetField === 'homeBookAuthor' ? 'Image uploaded. Click Save Book Card to publish it.' : 'Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally { setUploading(false); }
  };

  const saveSetting = async (key, value, label) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings/${key}`, { value }, { headers: getAuthHeader() });
      toast.success(`${label} saved!`);
    } catch { toast.error(`Failed to save ${label}`); }
    finally { setSaving(false); }
  };

  const savePageContent = async (pageKey) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings/page_${pageKey}`, { value: pageContents[pageKey] }, { headers: getAuthHeader() });
      toast.success('Page content saved!');
    } catch { toast.error('Failed to save page content'); }
    finally { setSaving(false); }
  };

  const updatePageContent = (pageKey, field, value) => {
    setPageContents(prev => ({ ...prev, [pageKey]: { ...prev[pageKey], [field]: value } }));
  };

  const updateSection = (group, field, value) => {
    setSections(prev => ({
      ...prev,
      [group]: {
        ...(prev?.[group] || {}),
        [field]: value
      }
    }));
  };

  const saveSections = () => saveSetting('site_sections', sections, 'Visibility settings');

  const addBookFeature = () => setBookInfo({ ...bookInfo, features: [...(bookInfo.features || []), ''] });
  const updateBookFeature = (idx, value) => {
    const features = [...(bookInfo.features || [])];
    features[idx] = value;
    setBookInfo({ ...bookInfo, features });
  };
  const removeBookFeature = (idx) => setBookInfo({ ...bookInfo, features: (bookInfo.features || []).filter((_, i) => i !== idx) });

  const ToggleRow = ({ label, description, checked, onChange }) => (
    <label className="flex items-start justify-between gap-4 rounded-xl border-2 border-sage-100 bg-white p-4">
      <span>
        <span className="block font-medium text-gray-900">{label}</span>
        {description && <span className="block text-sm text-gray-500 mt-1">{description}</span>}
      </span>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 accent-sage-700"
      />
    </label>
  );

  // FAQ CRUD
  const createFaq = async () => {
    if (!newFaq.question || !newFaq.answer) { toast.error('Please fill both question and answer'); return; }
    try {
      const response = await axios.post(`${API_URL}/admin/faqs`, newFaq, { headers: getAuthHeader() });
      setFaqs([...faqs, response.data]);
      setNewFaq({ question: '', answer: '' });
      toast.success('FAQ added!');
    } catch { toast.error('Failed to add FAQ'); }
  };

  const updateFaq = async (faqId) => {
    if (!editingFaq) return;
    try {
      await axios.put(`${API_URL}/admin/faqs/${faqId}`, editingFaq, { headers: getAuthHeader() });
      setFaqs(faqs.map(f => f.id === faqId ? { ...f, ...editingFaq } : f));
      setEditingFaq(null);
      toast.success('FAQ updated!');
    } catch { toast.error('Failed to update FAQ'); }
  };

  const deleteFaq = async (faqId) => {
    try {
      await axios.delete(`${API_URL}/admin/faqs/${faqId}`, { headers: getAuthHeader() });
      setFaqs(faqs.filter(f => f.id !== faqId));
      toast.success('FAQ deleted!');
    } catch { toast.error('Failed to delete FAQ'); }
  };

  // Testimonials
  const addTestimonial = () => setTestimonials([...testimonials, { id: Date.now().toString(), name: '', text: '', rating: 5 }]);
  const updateTestimonial = (idx, field, val) => { const t = [...testimonials]; t[idx][field] = val; setTestimonials(t); };
  const removeTestimonial = (idx) => setTestimonials(testimonials.filter((_, i) => i !== idx));

  // Certifications
  const addCertification = () => setAboutContent({ ...aboutContent, certifications: [...aboutContent.certifications, ''] });
  const updateCertification = (idx, val) => { const c = [...aboutContent.certifications]; c[idx] = val; setAboutContent({ ...aboutContent, certifications: c }); };
  const removeCertification = (idx) => setAboutContent({ ...aboutContent, certifications: aboutContent.certifications.filter((_, i) => i !== idx) });

  if (loading) return <AdminLayout title="Page Content"><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Manage Page Content">
      <Tabs defaultValue="homepage" className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 w-full max-w-4xl">
          <TabsTrigger value="homepage">Homepage</TabsTrigger>
          <TabsTrigger value="visibility">Visibility</TabsTrigger>
          <TabsTrigger value="book">Book</TabsTrigger>
          <TabsTrigger value="booking">Booking</TabsTrigger>
          <TabsTrigger value="about">About</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
          <TabsTrigger value="faqs">FAQs</TabsTrigger>
          <TabsTrigger value="pages">Page Headers</TabsTrigger>
        </TabsList>

        {/* Homepage Tab */}
        <TabsContent value="homepage">
          <HomeSectionsEditor />
          <Card className="border-0 shadow-lg mb-6">
            <CardContent className="p-8 space-y-6">
              <h3 className="text-2xl font-serif">Skincare Announcement Banner</h3>
              <p className="text-sm text-gray-600">Appears directly below the top navigation on desktop and mobile. Change the launch announcement, brand name, picture, button and destination any time.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <ToggleRow label="Show Skincare Banner" checked={(sections?.skincare?.enabled ?? SKINCARE_DEFAULTS.enabled) === true} onChange={value => updateSection('skincare', 'enabled', value)} />
                <ToggleRow label="Homepage Only" description="Turn off to show on every public page." checked={sections?.skincare?.homepageOnly === true} onChange={value => updateSection('skincare', 'homepageOnly', value)} />
                <ToggleRow label="Open Button Link in New Tab" checked={sections?.skincare?.newTab === true} onChange={value => updateSection('skincare', 'newTab', value)} />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  ['badge', 'Badge / Launch Status'], ['title', 'Headline / Brand Name'],
                  ['text', 'Announcement Text'], ['buttonText', 'Button Text'],
                  ['linkUrl', 'Button Link'], ['imageUrl', 'Picture URL (optional)']
                ].map(([field, label]) => <div key={field}>
                  <label htmlFor={`skincare-${field}`} className="block text-sm font-medium mb-2">{label}</label>
                  <input id={`skincare-${field}`} type="text" value={sections?.skincare?.[field] ?? SKINCARE_DEFAULTS[field]} onChange={e => updateSection('skincare', field, e.target.value)} placeholder={field === 'linkUrl' ? 'https://your-store.com or /contact' : ''} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>)}
              </div>
              <div>
                <label htmlFor="skincare-upload" className="block text-sm font-medium mb-2">Upload Banner Picture (optional)</label>
                <input id="skincare-upload" type="file" accept="image/*" disabled={uploading} onChange={e => handleImageUpload(e, 'skincareBanner')} className="w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700" />
                {sections?.skincare?.imageUrl && <Button variant="outline" size="sm" onClick={() => updateSection('skincare', 'imageUrl', '')} className="mt-2">Remove Picture</Button>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  ['backgroundColor', 'Background'], ['textColor', 'Text Colour'],
                  ['buttonColor', 'Button Colour'], ['buttonTextColor', 'Button Text Colour']
                ].map(([field, label]) => <div key={field}>
                  <label htmlFor={`skincare-${field}`} className="block text-sm font-medium mb-2">{label}</label>
                  <input id={`skincare-${field}`} type="color" value={sections?.skincare?.[field] || SKINCARE_DEFAULTS[field]} onChange={e => updateSection('skincare', field, e.target.value)} className="w-full h-10 rounded-lg border border-sage-200 cursor-pointer" />
                </div>)}
              </div>
              <div className="space-y-3">
                <p className="text-sm font-medium">Banner Preview {sections?.skincare?.enabled === false ? '(currently switched off)' : ''}</p>
                <SkincareBanner settings={sections?.skincare} preview />
              </div>
              <p className="text-xs text-gray-600">Use a full https:// link or an internal path such as /contact. An empty button label or link hides the button.</p>
              <Button disabled={saving || uploading} onClick={() => {
                const value = { ...SKINCARE_DEFAULTS, ...sections?.skincare };
                if (value.linkUrl && !skincareLink(value.linkUrl)) { toast.error('Please enter an http(s) link or an internal path starting with /.'); return; }
                saveSetting('site_sections', { ...sections, skincare: value }, 'Skincare banner');
              }} className="bg-sage-700 hover:bg-sage-800 text-white"><Save className="mr-2 h-4 w-4" />{uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Skincare Banner'}</Button>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-serif">Homepage Hero Section</h3>
                <Button onClick={() => navigate('/')} variant="outline" size="sm" data-testid="preview-homepage-btn"><Eye className="mr-2 h-4 w-4" />Preview</Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Badge Text</label>
                  <input data-testid="home-badge-input" type="text" value={homeHero.badge} onChange={e => setHomeHero({ ...homeHero, badge: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Main Title</label>
                    <input data-testid="home-title-input" type="text" value={homeHero.title} onChange={e => setHomeHero({ ...homeHero, title: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Subtitle</label>
                    <input data-testid="home-subtitle-input" type="text" value={homeHero.subtitle} onChange={e => setHomeHero({ ...homeHero, subtitle: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea data-testid="home-description-input" value={homeHero.description} onChange={e => setHomeHero({ ...homeHero, description: e.target.value })} rows={3} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Hero Image</label>
                  <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'homeHero')} disabled={uploading} data-testid="home-image-upload" className="w-full px-4 py-3 rounded-lg border-2 border-sage-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer" />
                  {homeHero.heroImage && (
                    <div className="mt-4">
                      <ImagePositionEditor
                        src={homeHero.heroImage}
                        position={homeHero.heroImagePosition}
                        onPositionChange={(pos) => setHomeHero({ ...homeHero, heroImagePosition: pos })}
                        aspectRatio="1/1"
                        label="Hero Image Position"
                      />
                    </div>
                  )}
                </div>
              </div>
              <Button onClick={() => saveSetting('home_hero', homeHero, 'Homepage')} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="save-homepage-btn">
                <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Homepage'}
              </Button>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg mt-6">
            <CardContent className="p-8 space-y-6">
              <h3 className="text-2xl font-serif">Homepage Floating Book Card</h3>
              <p className="text-sm text-gray-600">Featured in the opening hero, first on mobile. Customize this card independently. Empty title, author, cover or purchase link fields use the Book tab’s settings.</p>
              <ToggleRow label="Show Floating Book Card" description="The book must also be enabled in Visibility." checked={homeHero.bookCard?.enabled !== false} onChange={enabled => setHomeHero(prev => ({ ...prev, bookCard: { ...prev.bookCard, enabled } }))} />
              <div className="grid lg:grid-cols-2 gap-8 items-start">
                <div className="space-y-4">
                  {[
                    ['title', 'Book Title (optional override)'],
                    ['author', 'Author (optional override)'],
                    ['eyebrow', 'Small Heading'],
                    ['sealText', 'Cover Badge Text'],
                    ['subtitle', 'Subtitle'],
                    ['description', 'Short Description'],
                    ['buttonText', 'Purchase Button Text'],
                    ['purchaseUrl', 'Homepage Purchase Link (optional override)']
                  ].map(([field, label]) => (
                    <div key={field}>
                      <label htmlFor={`home-book-${field}`} className="block text-sm font-medium mb-2">{label}</label>
                      <input id={`home-book-${field}`} type={field === 'purchaseUrl' ? 'url' : 'text'} value={homeHero.bookCard?.[field] ?? HOME_BOOK_DEFAULTS[field]} placeholder={field === 'purchaseUrl' ? bookInfo.purchase_link || 'Uses Purchase Link from the Book tab' : ''} onChange={e => setHomeHero(prev => ({ ...prev, bookCard: { ...prev.bookCard, [field]: e.target.value } }))} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                    </div>
                  ))}
                  {[
                    ['coverImage', 'Book Cover Picture', 'homeBookCover'],
                    ['authorImage', 'Author Picture', 'homeBookAuthor']
                  ].map(([field, label, uploadTarget]) => (
                    <div key={field} className="space-y-2">
                      <label htmlFor={`home-book-${field}`} className="block text-sm font-medium">{label}</label>
                      <input id={`home-book-${field}`} aria-label={`${label} URL`} type="text" placeholder="Use default image, or enter an image URL" value={homeHero.bookCard?.[field] || ''} onChange={e => setHomeHero(prev => ({ ...prev, bookCard: { ...prev.bookCard, [field]: e.target.value } }))} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200" />
                      <input aria-label={`Upload ${label}`} type="file" accept="image/*" disabled={uploading} onChange={e => handleImageUpload(e, uploadTarget)} className="w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700" />
                      {homeHero.bookCard?.[field] && <Button size="sm" variant="outline" onClick={() => setHomeHero(prev => ({ ...prev, bookCard: { ...prev.bookCard, [field]: '' } }))}>Use Default Picture</Button>}
                    </div>
                  ))}
                  <p className="text-xs text-gray-600">Leave the link empty to use the Book tab’s Purchase Link. You can change it any time.</p>
                  <Button onClick={() => saveSetting('home_hero', homeHero, 'Homepage book card')} disabled={saving || uploading} className="bg-sage-700 hover:bg-sage-800 text-white"><Save className="mr-2 h-4 w-4" />{uploading ? 'Uploading...' : saving ? 'Saving...' : 'Save Book Card'}</Button>
                </div>
                <div><p className="text-sm font-medium mb-3">Card Preview</p><HomeBookCard book={bookInfo} settings={homeHero.bookCard} photo={homeHero.heroImage} /></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Visibility Tab */}
        <TabsContent value="visibility">
          <div className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-2xl font-serif">Navigation Links</h3>
                  <Button onClick={() => navigate('/')} variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" />Preview</Button>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <ToggleRow label="About Lindsey" checked={sections?.navigation?.about !== false} onChange={value => updateSection('navigation', 'about', value)} />
                  <ToggleRow label="Q&A" checked={sections?.navigation?.qa !== false} onChange={value => updateSection('navigation', 'qa', value)} />
                  <ToggleRow label="Downloads" checked={sections?.navigation?.downloads !== false} onChange={value => updateSection('navigation', 'downloads', value)} />
                  <ToggleRow label="Booking" description="Also hides the booking link when booking is unavailable." checked={sections?.navigation?.booking !== false} onChange={value => updateSection('navigation', 'booking', value)} />
                  <ToggleRow label="Blog" checked={sections?.navigation?.blog !== false} onChange={value => updateSection('navigation', 'blog', value)} />
                  <ToggleRow label="My Book" description="Also hides the book link when the book page is unavailable." checked={sections?.navigation?.book !== false} onChange={value => updateSection('navigation', 'book', value)} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 space-y-6">
                <h3 className="text-2xl font-serif">Homepage Sections</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ToggleRow label="Intro Section" checked={sections?.homepage?.intro !== false} onChange={value => updateSection('homepage', 'intro', value)} />
                  <ToggleRow label="Service Cards Section" checked={sections?.homepage?.services !== false} onChange={value => updateSection('homepage', 'services', value)} />
                  <ToggleRow label="Testimonials Section" checked={sections?.homepage?.testimonials !== false} onChange={value => updateSection('homepage', 'testimonials', value)} />
                  <ToggleRow label="Bottom Call-to-Action" checked={sections?.homepage?.cta !== false} onChange={value => updateSection('homepage', 'cta', value)} />
                  <ToggleRow label="Booking Service Card" checked={sections?.homepage?.bookingCard !== false} onChange={value => updateSection('homepage', 'bookingCard', value)} />
                  <ToggleRow label="Book Service Card" checked={sections?.homepage?.bookCard !== false} onChange={value => updateSection('homepage', 'bookCard', value)} />
                  <ToggleRow label="Downloads Service Card" checked={sections?.homepage?.downloadsCard !== false} onChange={value => updateSection('homepage', 'downloadsCard', value)} />
                  <ToggleRow label="Community Service Card" checked={sections?.homepage?.communityCard !== false} onChange={value => updateSection('homepage', 'communityCard', value)} />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 space-y-6">
                <h3 className="text-2xl font-serif">Book & Booking Availability</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ToggleRow label="Book Page Available" description="When off, visitors see a simple unavailable message." checked={sections?.book?.enabled !== false} onChange={value => updateSection('book', 'enabled', value)} />
                  <ToggleRow label="Show Book Price" checked={sections?.book?.showPrice !== false} onChange={value => updateSection('book', 'showPrice', value)} />
                  <ToggleRow label="Show Book Reviews" checked={sections?.book?.showReviews !== false} onChange={value => updateSection('book', 'showReviews', value)} />
                  <ToggleRow label="Booking Page Available" description="When off, visitors see direct contact options instead of the booking form." checked={sections?.booking?.enabled !== false} onChange={value => updateSection('booking', 'enabled', value)} />
                  <ToggleRow label="Show Booking Prices" checked={sections?.booking?.showPrices !== false} onChange={value => updateSection('booking', 'showPrices', value)} />
                  <ToggleRow label="Show Booking Help Block" checked={sections?.booking?.showHelp !== false} onChange={value => updateSection('booking', 'showHelp', value)} />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Book Unavailable Title</label>
                    <input type="text" value={sections?.book?.disabledTitle || ''} onChange={e => updateSection('book', 'disabledTitle', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Booking Unavailable Title</label>
                    <input type="text" value={sections?.booking?.disabledTitle || ''} onChange={e => updateSection('booking', 'disabledTitle', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Book Unavailable Message</label>
                    <textarea value={sections?.book?.disabledMessage || ''} onChange={e => updateSection('book', 'disabledMessage', e.target.value)} rows={3} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Booking Unavailable Message</label>
                    <textarea value={sections?.booking?.disabledMessage || ''} onChange={e => updateSection('booking', 'disabledMessage', e.target.value)} rows={3} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                  </div>
                </div>

                <Button onClick={saveSections} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                  <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Visibility'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Book Tab */}
        <TabsContent value="book">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-serif">Book Page & Purchase Area</h3>
                <Button onClick={() => navigate('/book')} variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" />Preview</Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Top Section Title</label>
                  <input type="text" value={pageContents.book?.title || ''} onChange={e => updatePageContent('book', 'title', e.target.value)} placeholder="My Book: Explant" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Book Title</label>
                  <input type="text" value={bookInfo.title || ''} onChange={e => setBookInfo({ ...bookInfo, title: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Top Section Description</label>
                  <textarea value={pageContents.book?.description || ''} onChange={e => updatePageContent('book', 'description', e.target.value)} rows={3} placeholder="Short text shown at the very top of the My Book page" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
                <ToggleRow label="Show Purchase Price" checked={sections?.book?.showPrice !== false} onChange={value => updateSection('book', 'showPrice', value)} />
                <ToggleRow label="Show Book Reviews" checked={sections?.book?.showReviews !== false} onChange={value => updateSection('book', 'showReviews', value)} />
                <div>
                  <label className="block text-sm font-medium mb-2">Author</label>
                  <input type="text" value={bookInfo.author || ''} onChange={e => setBookInfo({ ...bookInfo, author: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Price</label>
                  <input type="number" step="0.01" value={bookInfo.price || 0} onChange={e => setBookInfo({ ...bookInfo, price: parseFloat(e.target.value) || 0 })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Currency</label>
                  <input type="text" value={bookInfo.currency || 'USD'} onChange={e => setBookInfo({ ...bookInfo, currency: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Purchase Price Display</label>
                  <input type="text" value={bookInfo.priceDisplay || ''} onChange={e => setBookInfo({ ...bookInfo, priceDisplay: e.target.value })} placeholder="$29.99 CAD, Available through retailers, or leave blank" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea value={bookInfo.description || ''} onChange={e => setBookInfo({ ...bookInfo, description: e.target.value })} rows={4} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Purchase Link</label>
                  <input type="url" value={bookInfo.purchase_link || ''} onChange={e => setBookInfo({ ...bookInfo, purchase_link: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Purchase Button Text</label>
                  <input type="text" value={sections?.book?.buttonText || ''} onChange={e => updateSection('book', 'buttonText', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Purchase Note</label>
                  <input type="text" value={sections?.book?.redirectNote || ''} onChange={e => updateSection('book', 'redirectNote', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Book Cover Image</label>
                <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'bookCover')} disabled={uploading} className="w-full px-4 py-3 rounded-lg border-2 border-sage-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer" />
                {bookInfo.coverImage && (
                  <div className="mt-4 max-w-md">
                    <ImagePositionEditor
                      src={bookInfo.coverImage}
                      position={bookInfo.coverImagePosition}
                      onPositionChange={(pos) => setBookInfo({ ...bookInfo, coverImagePosition: pos })}
                      aspectRatio="3/4"
                      label="Book Cover Position"
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium">What's Inside Features</label>
                  <Button onClick={addBookFeature} size="sm" variant="outline"><Plus className="mr-2 h-4 w-4" />Add</Button>
                </div>
                <div className="space-y-2">
                  {(bookInfo.features || []).map((feature, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <input type="text" value={feature} onChange={e => updateBookFeature(i, e.target.value)} className="flex-1 px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" placeholder="Feature" />
                      <Button onClick={() => removeBookFeature(i)} size="sm" variant="outline" className="border-red-300 text-red-700"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => savePageContent('book')} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                  <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Top Text'}
                </Button>
                <Button onClick={() => saveSetting('book_info', bookInfo, 'Book information')} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                  <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Book Info'}
                </Button>
                <Button onClick={saveSections} disabled={saving} variant="outline" className="border-sage-300 text-sage-700">
                  <Save className="mr-2 h-4 w-4" />Save Book Controls
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Booking Tab */}
        <TabsContent value="booking">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-serif">Booking Page Text & Pricing Display</h3>
                <Button onClick={() => navigate('/booking')} variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" />Preview</Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <ToggleRow label="Booking Page Available" checked={sections?.booking?.enabled !== false} onChange={value => updateSection('booking', 'enabled', value)} />
                <ToggleRow label="Show Pricing on Booking Page" checked={sections?.booking?.showPrices !== false} onChange={value => updateSection('booking', 'showPrices', value)} />
                <ToggleRow label="Show Help Block" checked={sections?.booking?.showHelp !== false} onChange={value => updateSection('booking', 'showHelp', value)} />
                <div>
                  <label className="block text-sm font-medium mb-2">Payment Button Text</label>
                  <input type="text" value={sections?.booking?.buttonText || ''} onChange={e => updateSection('booking', 'buttonText', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Hero Title</label>
                  <input type="text" value={pageContents.booking?.title || ''} onChange={e => updatePageContent('booking', 'title', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Session Section Title</label>
                  <input type="text" value={pageContents.booking?.sessionTitle || ''} onChange={e => updatePageContent('booking', 'sessionTitle', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Hero Description</label>
                  <textarea value={pageContents.booking?.description || ''} onChange={e => updatePageContent('booking', 'description', e.target.value)} rows={3} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Form Title</label>
                  <input type="text" value={pageContents.booking?.formTitle || ''} onChange={e => updatePageContent('booking', 'formTitle', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Summary Title</label>
                  <input type="text" value={pageContents.booking?.summaryTitle || ''} onChange={e => updatePageContent('booking', 'summaryTitle', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Name Label</label>
                  <input type="text" value={pageContents.booking?.nameLabel || ''} onChange={e => updatePageContent('booking', 'nameLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Name Placeholder</label>
                  <input type="text" value={pageContents.booking?.namePlaceholder || ''} onChange={e => updatePageContent('booking', 'namePlaceholder', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email Label</label>
                  <input type="text" value={pageContents.booking?.emailLabel || ''} onChange={e => updatePageContent('booking', 'emailLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email Placeholder</label>
                  <input type="text" value={pageContents.booking?.emailPlaceholder || ''} onChange={e => updatePageContent('booking', 'emailPlaceholder', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Label</label>
                  <input type="text" value={pageContents.booking?.phoneLabel || ''} onChange={e => updatePageContent('booking', 'phoneLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Placeholder</label>
                  <input type="text" value={pageContents.booking?.phonePlaceholder || ''} onChange={e => updatePageContent('booking', 'phonePlaceholder', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Date Label</label>
                  <input type="text" value={pageContents.booking?.dateLabel || ''} onChange={e => updatePageContent('booking', 'dateLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Time Label</label>
                  <input type="text" value={pageContents.booking?.timeLabel || ''} onChange={e => updatePageContent('booking', 'timeLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Time Placeholder</label>
                  <input type="text" value={pageContents.booking?.timePlaceholder || ''} onChange={e => updatePageContent('booking', 'timePlaceholder', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">WhatsApp Button Text</label>
                  <input type="text" value={pageContents.booking?.whatsappLabel || ''} onChange={e => updatePageContent('booking', 'whatsappLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Call Button Text</label>
                  <input type="text" value={pageContents.booking?.callLabel || ''} onChange={e => updatePageContent('booking', 'callLabel', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Help Title</label>
                  <input type="text" value={pageContents.booking?.helpTitle || ''} onChange={e => updatePageContent('booking', 'helpTitle', e.target.value)} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Help Text</label>
                  <textarea value={pageContents.booking?.helpText || ''} onChange={e => updatePageContent('booking', 'helpText', e.target.value)} rows={3} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => savePageContent('booking')} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white">
                  <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Booking Text'}
                </Button>
                <Button onClick={saveSections} disabled={saving} variant="outline" className="border-sage-300 text-sage-700">
                  <Save className="mr-2 h-4 w-4" />Save Booking Controls
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Tab */}
        <TabsContent value="about">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-serif">About Lindsey Page</h3>
                <Button onClick={() => navigate('/about')} variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" />Preview</Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Biography</label>
                  <textarea value={aboutContent.bio} onChange={e => setAboutContent({ ...aboutContent, bio: e.target.value })} rows={6} data-testid="about-bio-input" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Mission Statement</label>
                  <textarea value={aboutContent.mission} onChange={e => setAboutContent({ ...aboutContent, mission: e.target.value })} rows={4} data-testid="about-mission-input" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Profile Image</label>
                  <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'aboutImage')} disabled={uploading} data-testid="about-image-upload" className="w-full px-4 py-3 rounded-lg border-2 border-sage-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer" />
                  {aboutContent.image && (
                    <div className="mt-4">
                      <ImagePositionEditor
                        src={aboutContent.image}
                        position={aboutContent.imagePosition}
                        onPositionChange={(pos) => setAboutContent({ ...aboutContent, imagePosition: pos })}
                        aspectRatio="4/5"
                        label="Profile Image Position"
                      />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium">Certifications</label>
                    <Button onClick={addCertification} size="sm" variant="outline"><Plus className="mr-2 h-4 w-4" />Add</Button>
                  </div>
                  <div className="space-y-2">
                    {aboutContent.certifications?.map((cert, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <input type="text" value={cert} onChange={e => updateCertification(i, e.target.value)} className="flex-1 px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" placeholder="Certification name" />
                        <Button onClick={() => removeCertification(i)} size="sm" variant="outline" className="border-red-300 text-red-700"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Button onClick={() => saveSetting('about_lindsey', aboutContent, 'About Page')} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="save-about-btn">
                <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save About Page'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-serif">Testimonials</h3>
                <Button onClick={addTestimonial} size="sm" className="bg-sage-700 text-white" data-testid="add-testimonial-btn"><Plus className="mr-2 h-4 w-4" />Add</Button>
              </div>
              <div className="space-y-4">
                {testimonials.map((t, i) => (
                  <Card key={t.id} className="border-2 border-sage-200">
                    <CardContent className="p-6 space-y-3">
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold">Testimonial {i + 1}</h4>
                        <Button onClick={() => removeTestimonial(i)} size="sm" variant="outline" className="border-red-300 text-red-700"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Name</label>
                          <input type="text" value={t.name} onChange={e => updateTestimonial(i, 'name', e.target.value)} className="w-full px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">Rating</label>
                          <select value={t.rating} onChange={e => updateTestimonial(i, 'rating', parseInt(e.target.value))} className="w-full px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none">
                            <option value={5}>5 Stars</option><option value={4}>4 Stars</option><option value={3}>3 Stars</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1">Text</label>
                        <textarea value={t.text} onChange={e => updateTestimonial(i, 'text', e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Button onClick={() => saveSetting('testimonials', testimonials, 'Testimonials')} disabled={saving} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="save-testimonials-btn">
                <Save className="mr-2 h-4 w-4" />{saving ? 'Saving...' : 'Save Testimonials'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-serif">Manage FAQs</h3>
                <Button onClick={() => navigate('/qa')} variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" />Preview</Button>
              </div>

              {/* Add New FAQ */}
              <Card className="border-2 border-dashed border-sage-300 bg-sage-50/50">
                <CardContent className="p-6 space-y-3">
                  <h4 className="font-semibold text-sage-800">Add New FAQ</h4>
                  <input type="text" value={newFaq.question} onChange={e => setNewFaq({ ...newFaq, question: e.target.value })} placeholder="Question" data-testid="new-faq-question" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                  <textarea value={newFaq.answer} onChange={e => setNewFaq({ ...newFaq, answer: e.target.value })} placeholder="Answer" rows={3} data-testid="new-faq-answer" className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                  <Button onClick={createFaq} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="add-faq-btn"><Plus className="mr-2 h-4 w-4" />Add FAQ</Button>
                </CardContent>
              </Card>

              {/* Existing FAQs */}
              <div className="space-y-4">
                {faqs.map(faq => (
                  <Card key={faq.id} className="border-2 border-sage-200">
                    <CardContent className="p-6 space-y-3">
                      {editingFaq?.id === faq.id ? (
                        <>
                          <input type="text" value={editingFaq.question} onChange={e => setEditingFaq({ ...editingFaq, question: e.target.value })} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                          <textarea value={editingFaq.answer} onChange={e => setEditingFaq({ ...editingFaq, answer: e.target.value })} rows={3} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                          <div className="flex space-x-2">
                            <Button onClick={() => updateFaq(faq.id)} size="sm" className="bg-sage-700 text-white"><Save className="mr-2 h-4 w-4" />Save</Button>
                            <Button onClick={() => setEditingFaq(null)} size="sm" variant="outline">Cancel</Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex justify-between items-start">
                            <h4 className="font-semibold text-gray-900 flex-1 pr-4">{faq.question}</h4>
                            <div className="flex space-x-2 flex-shrink-0">
                              <Button onClick={() => setEditingFaq({ id: faq.id, question: faq.question, answer: faq.answer })} size="sm" variant="outline"><Edit2 className="h-4 w-4" /></Button>
                              <Button onClick={() => deleteFaq(faq.id)} size="sm" variant="outline" className="border-red-300 text-red-700"><Trash2 className="h-4 w-4" /></Button>
                            </div>
                          </div>
                          <p className="text-gray-600 text-sm">{faq.answer}</p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {faqs.length === 0 && <p className="text-gray-500 text-center py-8">No FAQs yet. Add your first one above.</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Page Headers Tab */}
        <TabsContent value="pages">
          <div className="space-y-6">
            {pageKeys.map(key => (
              <Card key={key} className="border-0 shadow-lg">
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold capitalize">{key} Page</h3>
                    <Button onClick={() => navigate(`/${key === 'qa' ? 'qa' : key}`)} variant="outline" size="sm"><Eye className="mr-2 h-4 w-4" />Preview</Button>
                  </div>
                  <div className="grid md:grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Page Title</label>
                      <input type="text" value={pageContents[key]?.title || ''} onChange={e => updatePageContent(key, 'title', e.target.value)} data-testid={`page-title-${key}`} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Page Description</label>
                      <textarea value={pageContents[key]?.description || ''} onChange={e => updatePageContent(key, 'description', e.target.value)} rows={2} data-testid={`page-desc-${key}`} className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none" />
                    </div>
                  </div>
                  <Button onClick={() => savePageContent(key)} disabled={saving} size="sm" className="bg-sage-700 hover:bg-sage-800 text-white" data-testid={`save-page-${key}`}>
                    <Save className="mr-2 h-4 w-4" />Save
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminContent;
